// frontend/src/pages/Landing.jsx
// ✅ GOOGLE OAUTH VERSION with Video + Animations

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Screen from "../uizard/ui/Screen"
import { useAuth } from "../lib/useAuth";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";
  
  const [error, setError] = useState("");

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  // If already authenticated, don't show anything
  if (user) {
    return null;
  }

  return (
    <Screen>
      <div className="landing-wrapper">
        {/* Animated background elements */}
        <div className="bg-orbs">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
        </div>

        <div className="landing-container">
          {/* Hero section with video */}
          <div className="landing-hero">
            {/* ✅ YouTube Video Embed */}
            <div className="hero-video-container">
              <iframe
                className="hero-video"
                src="https://www.youtube.com/embed/Ne2rWNPKkwE?autoplay=1&loop=1&playlist=Ne2rWNPKkwE&mute=1"
                title="How ArtiBytes Works"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Content below video */}
            <div className="hero-content">
              <div className="logo-slogan-row">
                <img 
                  src="/logo.png" 
                  alt="ArtiBytes Logo" 
                  className="landing-logo"
                />
                <div className="slogan-wrapper">
                  <span className="slogan">Your Style, Your Economy</span>
                </div>
              </div>

              <h1 className="hero-title">ArtiBytes</h1>
              <p className="hero-subtitle">AI Image Generation with Creator Royalties</p>
              <p className="hero-tagline">Generate Images • Submit Styles • Earn Royalties</p>

              {/* ✅ Google Sign In Button */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleSignInButton />
              </div>

              {error && (
                <div className="error-box">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>

          {/* Stats row - ✅ Only 30% animates */}
          <div className="stats-row">
            <div className="stat-item stat-item-1">
              <div className="stat-value">Create</div>
              <div className="stat-label">Unique styles</div>
            </div>
            <div className="stat-divider stat-divider-animated"></div>
            <div className="stat-item stat-item-2">
              <div className="stat-value stat-bounce">30%</div>
              <div className="stat-label">Royalty per image</div>
            </div>
            <div className="stat-divider stat-divider-animated"></div>
            <div className="stat-item stat-item-3">
              <div className="stat-value">Grow</div>
              <div className="stat-label">Digital Assets</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Amatic+SC:wght@700&display=swap');

  .landing-wrapper {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
    background: #ffffff;
  }

  /* Animated background orbs */
  .bg-orbs {
    position: absolute;
    inset: 0;
    overflow: hidden;
    z-index: 0;
  }

  .orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.4;
    animation: float 20s ease-in-out infinite;
  }

  .orb-1 {
    width: 400px;
    height: 400px;
    background: linear-gradient(135deg, #6D5EF9, #A78BFA);
    top: -10%;
    left: -10%;
    animation-delay: 0s;
  }

  .orb-2 {
    width: 500px;
    height: 500px;
    background: linear-gradient(135deg, #3B82F6, #8B5CF6);
    bottom: -15%;
    right: -10%;
    animation-delay: 5s;
  }

  .orb-3 {
    width: 350px;
    height: 350px;
    background: linear-gradient(135deg, #1E3A8A, #6D28D9);
    top: 40%;
    right: 20%;
    animation-delay: 10s;
  }

  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(30px, -30px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }

  /* Container */
  .landing-container {
    max-width: 700px;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  /* Hero section */
  .landing-hero {
    text-align: center;
    padding: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 20px 60px rgba(109, 94, 249, 0.12);
    margin-bottom: 32px;
    overflow: hidden;
    animation: fadeInUp 0.8s ease-out;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* ✅ Video Container */
  .hero-video-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    overflow: hidden;
    background: #000;
  }

  .hero-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Content below video */
  .hero-content {
    padding: 48px 32px;
  }

  /* Logo and Slogan Row */
  .logo-slogan-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .landing-logo {
    width: 100px;
    height: 100px;
    animation: logoFloat 3s ease-in-out infinite;
    object-fit: contain;
    filter: drop-shadow(0 4px 12px rgba(109, 94, 249, 0.3));
    flex-shrink: 0;
  }

  @keyframes logoFloat {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  /* Slogan with animated reveal - AMATIC SC FONT */
  .slogan-wrapper {
    position: relative;
    overflow: hidden;
  }

  .slogan {
    font-family: 'Amatic SC', cursive;
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #6D5EF9 0%, #8B5CF6 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    white-space: nowrap;
    display: inline-block;
    animation: revealSlogan 2s ease-out forwards;
    opacity: 0;
    letter-spacing: 0.5px;
  }

  @keyframes revealSlogan {
    0% {
      opacity: 0;
      transform: translateX(-20px);
      filter: blur(4px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
      filter: blur(0);
    }
  }

  /* Add handwriting effect */
  .slogan::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    animation: writeEffect 2s ease-out forwards;
  }

  @keyframes writeEffect {
    0% {
      left: 0;
      right: 0;
    }
    100% {
      left: 100%;
      right: 0;
    }
  }

  .hero-title {
    margin: 0 0 12px 0;
    font-size: 48px;
    font-weight: 900;
    background: linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -0.02em;
  }

  .hero-subtitle {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
  }

  .hero-tagline {
    margin: 0 0 32px 0;
    font-size: 15px;
    color: #6B7280;
    font-weight: 500;
  }

  /* Error box */
  .error-box {
    margin-top: 20px;
    padding: 14px 18px;
    background: rgba(254, 242, 242, 0.95);
    border: 1px solid rgba(254, 202, 202, 0.8);
    border-radius: 12px;
    color: #B91C1C;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }

  .error-icon {
    font-size: 18px;
  }

  /* ✨ Stats row with animations */
  .stats-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    padding: 24px 32px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 8px 24px rgba(109, 94, 249, 0.08);
    animation: fadeInUp 1s ease-out 0.3s backwards;
  }

  .stat-item {
    text-align: center;
    opacity: 0;
    animation: fadeIn 0.6s ease-out forwards;
  }

  .stat-item-1 {
    animation-delay: 0.4s;
  }

  .stat-item-2 {
    animation-delay: 0.6s;
  }

  .stat-item-3 {
    animation-delay: 0.8s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .stat-value {
    font-size: 32px;
    font-weight: 900;
    background: linear-gradient(135deg, #6D5EF9 0%, #8B5CF6 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 4px;
    transition: all 0.3s ease;
  }

  /* ✨ Bounce animation for "30%" ONLY */
  .stat-bounce {
    animation: bounce 3s ease-in-out infinite;
  }

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  /* ✨ Hover effect for stat items */
  .stat-item:hover .stat-value {
    transform: scale(1.1);
    filter: brightness(1.2);
  }

  .stat-label {
    font-size: 13px;
    color: #6B7280;
    font-weight: 600;
    transition: color 0.3s ease;
  }

  .stat-item:hover .stat-label {
    color: #6D5EF9;
  }

  /* ✨ Animated divider */
  .stat-divider {
    width: 1px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, rgba(229, 231, 235, 0.6), transparent);
    position: relative;
    overflow: hidden;
  }

  .stat-divider-animated::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, 
      transparent 0%, 
      rgba(109, 94, 249, 0.6) 50%, 
      transparent 100%
    );
    animation: dividerGlow 3s ease-in-out infinite;
  }

  @keyframes dividerGlow {
    0%, 100% {
      transform: translateY(-100%);
      opacity: 0;
    }
    50% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .landing-wrapper {
      padding: 16px;
    }
    .landing-hero {
      border-radius: 20px;
    }
    .hero-content {
      padding: 32px 20px;
    }
    .logo-slogan-row {
      flex-direction: column;
      gap: 12px;
    }
    .landing-logo {
      width: 80px;
      height: 80px;
    }
    .slogan {
      font-size: 22px;
    }
    .hero-title {
      font-size: 36px;
    }
    .hero-subtitle {
      font-size: 18px;
    }
    .hero-tagline {
      font-size: 14px;
    }
    .stats-row {
      gap: 16px;
      padding: 20px 16px;
      flex-wrap: nowrap;
    }
    .stat-item {
      flex: 1;
      min-width: 0;
    }
    .stat-value {
      font-size: 24px;
    }
    .stat-label {
      font-size: 11px;
    }
    .stat-divider {
      width: 1px;
      height: 30px;
    }
    
    .orb {
      filter: blur(100px);
      opacity: 0.2;
    }
    
    .orb-1 {
      width: 250px;
      height: 250px;
    }
    
    .orb-2 {
      width: 300px;
      height: 300px;
    }
    
    .orb-3 {
      width: 200px;
      height: 200px;
    }
  }
`}</style>
    </Screen>
  );
}